import { GlobalStyle, LoadingWrapper, Main } from "./AllocationsPage.styles";
import { Navigate, useLocation, useSearchParams } from "react-router-dom";
import React, { useContext, useMemo, useState } from "react";

import { AllocationsProvider } from "features/allocations/contexts/Context";
import { AllocationsSignalRProvider } from "features/allocations/contexts/SignalRContext";
import { CustomGroupContextProvider } from "features/allocations/contexts/CustomGroupContext";
import { EmployeeGridSelectionProvider } from "features/allocations/contexts/EmployeeGridSelectionContext";
import { EmployeeRowExpansionContextProvider } from "features/allocations/contexts/EmployeeRowExpansionContext";
import { EmployeeRowsProvider } from "features/allocations/contexts/EmployeeRowsContext";
import FooterPanel from "features/allocations/components/FooterPanel";
import Grid from "features/allocations/components/grid/Grid";
import { GridUIContextProvider } from "features/allocations/contexts/GridUIContext";
import HeaderPanel from "features/allocations/components/HeaderPanel";
import Loading from "shared/components/feedback/loading/Loading";
import PillTooltip from "features/allocations/components/modals/PillTooltip";
import SessionContext from "app/contexts/SessionContext";
import { usePageAccess } from "shared/hooks/access/usePageAccess.js";

const MemoizedGrid = React.memo(Grid);
const MemoizedHeaderPanel = React.memo(HeaderPanel);
const MemoizedFooterPanel = React.memo(FooterPanel);

const usePathname = () => {
    const location = useLocation();
    return location.pathname;
};

const AllocationsPage = () => {
    const session = useContext(SessionContext);
    const [searchParams] = useSearchParams();

    // Primary access check: does the user have permission to view /allocations?
    const { authorized: allocationsAuthorized } = usePageAccess(
        usePathname(),
        session?.legacyId,
        session?.currentDepartmentId
    );

    // Secondary access check: does the user have permission to view /my-allocations?
    // This is used as a fallback when the primary check fails.
    const {
        authorized: myAllocationsAuthorized,
        isFetched: myAllocationsFetched,
    } = usePageAccess(
        globalThis.config.routes.myAllocations,
        session?.legacyId,
        session?.currentDepartmentId
    );

    // Capture the userId from the URL once at mount. The authorization decision
    // must not flip just because Apply (or any other action) rewrites the
    // search params later — that would unmount/remount HeaderPanel and fire
    // updateFilterSelection a second time against the backend.
    const [initialUrlUserId] = useState(() => searchParams.get("userId"));

    // Resolves the final authorization state:
    // 1. If the user has direct access to /allocations → authorized.
    // 2. If not, and the /my-allocations check has completed → authorized only if
    //    the user has access to /my-allocations AND the userId in the URL matches their own.
    //    This allows users without broad allocation permissions to view their own allocations.
    // 3. Returns null while either check is still pending (used to show a loading state).
    const authorized = useMemo(() => {
        if (allocationsAuthorized) return true;
        if (allocationsAuthorized !== null && myAllocationsFetched) {
            return (
                !!myAllocationsAuthorized &&
                initialUrlUserId === String(session?.legacyId)
            );
        }
        return null;
    }, [
        allocationsAuthorized,
        myAllocationsAuthorized,
        myAllocationsFetched,
        initialUrlUserId,
        session?.legacyId,
    ]);

    // Show loading spinner until authorization is resolved (null = still pending).
    const isLoading = authorized === null;

    globalThis.onbeforeunload = function (e) {};

    globalThis.stickyHeader = () => {
        const header = document.getElementById("date_cols_wrapper");
        if (!header) return;
        const headerBottom = header.getBoundingClientRect().bottom; // + globalThis.pageYOffset;
        const headers = document.getElementsByClassName("allocEmpHeader");
        for (let i = 0; i < headers.length; i++) {
            const id = headers[i].id.replace("emp_header_wrapper_", "div_");
            const divEmpElem = document.getElementById(id);
            if (!divEmpElem) continue;
            const divEmp = divEmpElem.getBoundingClientRect().top;
            const divEmpTop = i === 0 ? divEmp : divEmp - 35;
            if (headerBottom > divEmpTop) {
                headers[i].classList.add("allocationsStickyEmployee");
                headers[i].style.top = headerBottom + "px";
            } else {
                headers[i].classList.remove("allocationsStickyEmployee");
                headers[i].style.top = 0;
            }
        }
    };

    globalThis.onscroll = () => {
        globalThis.scroll({ behavior: "smooth" });
        globalThis.stickyHeader();
    };

    const renderComponents = (
        <AllocationsSignalRProvider>
            <AllocationsProvider>
                <GridUIContextProvider>
                    <GlobalStyle></GlobalStyle>
                    <Main id="gridContainer">
                        <EmployeeRowExpansionContextProvider>
                            <EmployeeGridSelectionProvider>
                                <EmployeeRowsProvider>
                                    <CustomGroupContextProvider>
                                        <MemoizedHeaderPanel gridId="allocationsGrid" />
                                        <MemoizedGrid id="allocationsGrid" />
                                    </CustomGroupContextProvider>
                                </EmployeeRowsProvider>
                            </EmployeeGridSelectionProvider>
                        </EmployeeRowExpansionContextProvider>
                        <MemoizedFooterPanel />
                    </Main>
                    <PillTooltip />
                </GridUIContextProvider>
            </AllocationsProvider>
        </AllocationsSignalRProvider>
    );

    const renderPage = authorized ? (
        renderComponents
    ) : (
        <Navigate to={globalThis.config.routes.unauthorized} />
    );

    const renderLoading = (
        <LoadingWrapper>
            <Loading text="Loading Page" />
        </LoadingWrapper>
    );

    return <>{isLoading ? renderLoading : renderPage}</>;
};

export default AllocationsPage;
